import type Mapper from './props-mapper'
import { isObservableMap } from 'mobx'
import get from 'lodash/get'
import { uistore } from '../uistore'
import toJS from '../../utils/tojs'

function checkType(checkType, val) {
  let err
  if (val === undefined) {
    // checking against undefined here is interfering with error checks elsewhere
    return null
  } else if (checkType === 'string' && Array.isArray(val)) {
    err = `Expected a string value, got 'list'`
  } else if (checkType === 'string' && (typeof val === 'object' && val !== null)) {
    err = `Expected a string value, got 'object'`
  } else if (checkType === 'list' && !Array.isArray(val)) {
    err = `Expected a list value, got '${typeof val}'`
  } else if (checkType === 'number' && typeof val !== 'number') {
    err = `Expected a number value, got '${typeof val}'`
  } else if (checkType === 'object' && typeof val !== 'object') {
    err = `Expected an object value, got '${typeof val}'`
  } else if (checkType === 'list' || checkType === 'number' || checkType === 'object' || checkType === 'string') {
    err = ''
  }
  return err
}
export function resolveDataAtFieldPath(data: any, fieldPath: string) {
  if(!fieldPath || typeof data !== 'object') return data

  if(Array.isArray(data) && !/^\[?\d+/.test(fieldPath)) {
    return get(data[0], fieldPath)
  }

  return get(data, fieldPath)
}

export default function convertProps (this: Mapper['mapping'], data, childrenProps = {}, setError: Mapper['setError'], preserveUndefinedValues = false) {
  let propVal

  if (!this) return

  if (setError && this.bindingType) {
    let err = checkType(this.type, data)
    const expectingMissingValue = (this.bindingType === 'value' && this.children === null) || (this.bindingType === 'data' && !this.dataSource)
    if (this.bindingType === 'data' && !!this.value && data === undefined) {
      err = 'Bound to an undefined value'
    }
    if (err === '' || expectingMissingValue) {
      setError(null)
    } else if (err) {
      setError({ message: err }, 'warning')
    }
  }

  if (this.bindingType === 'null' || (data === null && this.bindingType === 'function')) return null

  const t = this.type

  switch (t) {
    case 'multiselect':
      if (data) {
        propVal = typeof data === 'string' ? [data] : data
      } else {
        if (Array.isArray(this.value) && this.value.length) {
          if ((this.value[0] as any).value) {
            propVal = this.value.map(v => v.value)
          } else {
            propVal = this.value
          }
        } else {
          propVal = []
        }
      }
      break
    case 'slider':
      if (Array.isArray(this.value) && this.value.length && this.value.length === 4) {
        if (this.bindingType === 'value') {
          propVal = this.value[3]
        } else {
          propVal = null
        }
      }
      break
    case 'Filter':      
    case 'Transformation':
      propVal = data
      break
    case 'object':
      propVal = parseObjVal(this, data, childrenProps, preserveUndefinedValues)
      break
    case 'Records':
      propVal = data && Array.isArray(data) && data.map(d => parseRecordVal(this, d))
      break
    case 'Record':
      propVal = parseRecordVal(this, data, childrenProps)
      break
    case 'list':
      propVal = parseListVal(this, data, childrenProps)
      break
    case 'FieldSet':
      propVal = parseFieldSet(parseListVal(this, data, childrenProps), this)
      break
    case 'PrimitiveArray':
      propVal = parsePrimitiveArray(this, data)
      break
    default:
      propVal = parsePrimitives(this, data)
      break
  }

  return propVal === null ? undefined : propVal
}

function parseFieldSet (value, mapping) {
  if (!Array.isArray(value)) return value
  value = value.filter(Boolean)
  for (const f of value) {
    if (mapping.dataSourceId && /(REFERENCE|PICKLIST)/.test(f.type)) {
      const ds = uistore?.dataSourceFactory?.getInstance(mapping.dataSourceId)
      if (!ds || !ds.config || !ds.config.fields) continue
      const field = ds.config.fields.find(ff => f.fieldPath === ff.fieldPath)
      if (field) {
        f.referenceData = field.referenceData
        f.picklist = field.picklist
      }
    }
  }
  return value
}

function parsePrimitiveArray (mapping, data) {
  let propVal
  if (data?.length) {
    if (data.filter && typeof data[0] !== 'object') {
      propVal = data.filter(x => x)
    } else if (mapping.value && mapping.value.fieldPath) {
      propVal = data && data.map(item => item[mapping.value.aliasedFieldPath])
    } else {
      propVal = data
    }
  }
  return propVal
}

function coerceToPrimitive (obj) {
  if (!obj) return obj
  if (Array.isArray(obj) && obj.length) {
    return coerceToPrimitive(obj[0])
  }
  if (typeof obj === 'object') {
    return coerceToPrimitive(obj[Object.keys(obj)[0]])
  }
  return obj
}

function parsePrimitives (mapping, data, multiType?: string) {
  let propVal

  if (data != null) {
    // if a parent gave us data and/or if bound directly to a datasource
    if (
      !mapping.componentMapping &&
      mapping.value &&
      mapping.value.fieldPath &&
      mapping.value.fieldPath.length > 0
    ) {
      // bound to a datasource field
      if (Array.isArray(data) && data.length) {
        propVal = resolveDataAtFieldPath(data, mapping.value.aliasedFieldPath)
      } else if (typeof data === 'object') {
        if (isObservableMap(data)) {
          propVal = data.get(mapping.value.aliasedFieldPath)
        } else propVal = get(data, mapping.value.aliasedFieldPath)
      } else {
        propVal = data
      }
    } else {
      // bound directly to a data source, no field mapping map the first
      // primitive self.value
      if (mapping.bindingType) propVal = coerceToPrimitive(data)
    }
  } else {
    if (mapping.value && mapping.value.groupBy) {
      propVal = mapping.value.fieldPath
    } else if (
      (mapping.type === 'number' || multiType === 'number') &&
      typeof mapping.value === 'string'
    ) {
      propVal = parseFloat(mapping.value)
    } else {
      if (mapping.bindingType === 'value') {
        propVal = mapping.value
      }
    }
  }

  let val = coerceToPrimitive(propVal)
  if (mapping.type === 'color' && val && val.length) {
    if (val.trimLeft().startsWith('var(')) return val
    val = convertColor(val)
  }
  return val
}

function convertColor (val) {
  if (typeof window !== 'undefined' && window.getComputedStyle && !window.__polystack_data__?.isStaticSite) {
    let el: HTMLDivElement = document.querySelector('#convert-color')
    if (!el) {
      el = document.createElement('div')
      el.id = 'convert-color'
      el.style.display = 'none'
      document.body.appendChild(el)
    }
    el.style.color = val
    val = getComputedStyle(el).color
  }
  return val
}

function parseListVal (mapping, data, childrenProps = {}) {
  if (data && Array.isArray(data)) return data.filter(x => x)

  if (!mapping.children) return

  if (!data) {
    return Object.values(childrenProps).filter(v => v !== undefined)
  }
}

/** pass preserveUndefinedValues to get also empty objects */
function parseObjVal (mapping, data, childrenProps = {}, preserveUndefinedValues = false) {
  let propVal = {}
  let d

  if (!data && !Object.keys(childrenProps)) return
  if (data && Array.isArray(data)) d = data[0]
  else if (data && typeof data === 'object') d = data

  // super edgy case to allow binding datasource to State as a reference
  if (d && d.constructor && d.constructor.name === 'DataStoreProvider') {
    return d
  }

  if (typeof d === 'object') propVal = { ...d }
  for (const p in childrenProps) {
    const v = childrenProps[p]
    if (v !== undefined || preserveUndefinedValues) propVal[p] = v
  }

  if (!Object.keys(propVal).length && mapping.type === 'object') return
  return propVal
}

function parseRecordVal (mapping, data, childrenProps = {}) {
  if (!mapping.dataSource && !mapping.value && !mapping.recordType) {
    return parseObjVal(mapping, data, childrenProps)
  }
  // observe the entire object, as we don't know what user JS functions might care about
  toJS(data)
  const Record = mapping.ItemClass
  let propVal = new Record({}, true)
  if (data) {
    if (Array.isArray(data)) {
      propVal = data.length && data[0]
    } else {
      propVal = data
    }
  }

  if (propVal && propVal.$ && !propVal.$.Record) propVal = new Record(propVal, true)

  for (const p in childrenProps) {
    const v = childrenProps[p]
    if (v !== undefined) propVal[p] = v
  }

  return propVal
}
